/**
 * HiringMetrics — Phase 5 (2026-02).
 *
 * Time-to-hire, funnel conversion, decline-reason breakdown, hires-by-month.
 * Deliberately no "source ROI" — we don't have cost-per-source data yet.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api, { errMsg } from '../../lib/api';
import { Card } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { TrendingUp, Timer, XCircle, GitBranch, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip,
         FunnelChart, Funnel, LabelList, PieChart, Pie, Cell } from 'recharts';

const C = {
  ink: '#2D2833', ink2: '#685B72', soft: '#8A7D95',
  border: '#E8E5EC', paper: '#FBF8F4',
  plum: '#4A3B5F', gold: '#B08A3A', teal: '#3B6F5D', rose: '#8A3A3A',
};
const FUNNEL_COLORS = ['#4A3B5F', '#5A4B75', '#685B72', '#7A6D82',
                        '#8A7D95', '#B08A3A', '#C7A24E', '#3B6F5D', '#2F5F4A'];
const REASON_COLORS = ['#8A3A3A', '#B08A3A', '#4A3B5F', '#3B6F5D', '#8A7D95', '#6E5F78'];

function Tile({ label, value, sub, testid }) {
  return (
    <Card className="p-3 border" style={{ borderColor: C.border }} data-testid={testid}>
      <div className="text-[10.5px] uppercase tracking-widest font-semibold" style={{ color: C.plum }}>{label}</div>
      <div className="font-serif text-3xl mt-1" style={{ color: C.ink }}>{value ?? '—'}</div>
      {sub && <div className="text-[11px] mt-1" style={{ color: C.soft }}>{sub}</div>}
    </Card>
  );
}

export default function HiringMetrics() {
  const [window, setWindow] = useState('180');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/hr/metrics/hiring?window_days=${window}`)
      .then(r => setData(r.data))
      .catch(e => toast.error(errMsg(e, 'Could not load metrics')))
      .finally(() => setLoading(false));
  }, [window]);
  useEffect(() => { load(); }, [load]);

  const funnelData = useMemo(() => (data?.funnel || []).map((f, i) => ({
    name: f.stage,
    value: f.n,
    conv: f.conversion_from_prev_pct,
    fill: FUNNEL_COLORS[i % FUNNEL_COLORS.length],
  })), [data]);

  const declineData = useMemo(() => (data?.decline_reasons || []).map((r, i) => ({
    name: r.reason,
    value: r.n,
    fill: REASON_COLORS[i % REASON_COLORS.length],
  })), [data]);

  return (
    <div className="space-y-4" data-testid="hr-hiring-metrics">
      {/* Filters */}
      <div className="flex items-center gap-2">
        <div className="text-[12px]" style={{ color: C.soft }}>Window</div>
        <Select value={window} onValueChange={setWindow}>
          <SelectTrigger className="h-8 w-[140px] text-[12px]" data-testid="metrics-window"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="60">Last 60 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="180">Last 180 days</SelectItem>
            <SelectItem value="365">Last 12 months</SelectItem>
            <SelectItem value="730">Last 24 months</SelectItem>
          </SelectContent>
        </Select>
        <button onClick={load} data-testid="metrics-refresh"
                className="ml-auto inline-flex items-center gap-1 text-[12px]"
                style={{ color: C.plum }}>
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {/* Tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Tile label="Time to hire (avg)"
              value={data?.time_to_hire?.avg_days != null ? `${data.time_to_hire.avg_days}d` : null}
              sub={`median ${data?.time_to_hire?.median_days ?? '—'}d · n=${data?.time_to_hire?.count ?? 0}`}
              testid="tile-tth" />
        <Tile label="Active in pipeline" value={data?.active_in_pipeline}
              sub="Excluding Hired / withdrawn"
              testid="tile-active" />
        <Tile label="Offers extended" value={data?.offers_in_window}
              sub={`Accept rate ${data?.accept_rate_pct ?? '—'}%`}
              testid="tile-offers" />
        <Tile label="Hires (window)" value={data?.hires_in_window}
              sub={`${data?.hires_by_month?.length || 0} active months`}
              testid="tile-hires" />
        <Tile label="Declines" value={(data?.decline_reasons || []).reduce((a, b) => a + b.n, 0)}
              sub="Rolled up below"
              testid="tile-declines" />
      </div>

      {/* Funnel */}
      <Card className="p-4 border" style={{ borderColor: C.border }} data-testid="metrics-funnel">
        <div className="flex items-center gap-2 mb-2">
          <GitBranch size={14} style={{ color: C.plum }} />
          <div className="font-serif text-lg" style={{ color: C.ink }}>Pipeline funnel</div>
        </div>
        <div className="text-[11px]" style={{ color: C.soft }}>
          Every candidate who created within the window and has EVER been at a stage counts once at that stage and every earlier one.
        </div>
        {loading ? (
          <div className="p-8 text-sm" style={{ color: C.soft }}>Loading…</div>
        ) : (
          <div style={{ height: 320 }} className="mt-3">
            <ResponsiveContainer>
              <FunnelChart>
                <Tooltip
                  formatter={(v, _n, p) => [`${v}${p?.payload?.conv != null ? ` (${p.payload.conv}% of prev)` : ''}`, p?.payload?.name]}
                />
                <Funnel dataKey="value" data={funnelData} isAnimationActive={true}>
                  <LabelList position="right" fill="#2D2833" stroke="none"
                             dataKey={(x) => `${x.name} — ${x.value}${x.conv != null ? ` (${x.conv}%)` : ''}`} />
                </Funnel>
              </FunnelChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Time in stage */}
        <Card className="p-4 border" style={{ borderColor: C.border }} data-testid="metrics-time-in-stage">
          <div className="flex items-center gap-2 mb-2">
            <Timer size={14} style={{ color: C.plum }} />
            <div className="font-serif text-lg" style={{ color: C.ink }}>Avg days per stage</div>
          </div>
          {loading ? (
            <div className="p-6 text-sm" style={{ color: C.soft }}>Loading…</div>
          ) : (data?.time_in_stage || []).length === 0 ? (
            <div className="p-6 text-sm" style={{ color: C.soft }}>Not enough stage-change data yet.</div>
          ) : (
            <div style={{ height: 280 }}>
              <ResponsiveContainer>
                <BarChart data={data.time_in_stage} layout="vertical" margin={{ left: 20 }}>
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="stage" width={140} tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v, _n, p) => [`${v} days (n=${p?.payload?.n})`, 'Avg']} />
                  <Bar dataKey="avg_days" fill={C.plum} radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        {/* Decline reasons */}
        <Card className="p-4 border" style={{ borderColor: C.border }} data-testid="metrics-decline-reasons">
          <div className="flex items-center gap-2 mb-2">
            <XCircle size={14} style={{ color: C.rose }} />
            <div className="font-serif text-lg" style={{ color: C.ink }}>Why we lose candidates</div>
          </div>
          {loading ? (
            <div className="p-6 text-sm" style={{ color: C.soft }}>Loading…</div>
          ) : declineData.length === 0 ? (
            <div className="p-6 text-sm" style={{ color: C.soft }}>No declines in this window.</div>
          ) : (
            <div style={{ height: 280 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Tooltip formatter={(v, n) => [`${v}`, n]} />
                  <Pie data={declineData} dataKey="value" nameKey="name"
                       cx="45%" cy="50%" outerRadius={90} label>
                    {declineData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </div>

      {/* Hires by month */}
      <Card className="p-4 border" style={{ borderColor: C.border }} data-testid="metrics-hires-by-month">
        <div className="flex items-center gap-2 mb-2">
          <TrendingUp size={14} style={{ color: C.teal }} />
          <div className="font-serif text-lg" style={{ color: C.ink }}>Hires by month</div>
        </div>
        {loading ? (
          <div className="p-6 text-sm" style={{ color: C.soft }}>Loading…</div>
        ) : (data?.hires_by_month || []).length === 0 ? (
          <div className="p-6 text-sm" style={{ color: C.soft }}>No hires in this window.</div>
        ) : (
          <div style={{ height: 240 }}>
            <ResponsiveContainer>
              <BarChart data={data.hires_by_month}>
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="hires" fill={C.teal} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>
    </div>
  );
}
